import * as d3 from "d3";
import {StaticDomTags} from "./static.dom.tags";
import {NodeService} from "../../../services/data/node.service";
import {TrainrunService} from "../../../services/data/trainrun.service";
import {TrainrunSectionService} from "../../../services/data/trainrunsection.service";
import {SVGMouseController} from "../../util/svg.mouse.controller";
import {NoteService} from "../../../services/data/note.service";
import {
  NODE_MIN_HEIGHT,
  NODE_MIN_WIDTH,
  NODE_TEXT_AREA_HEIGHT,
  RASTERING_BASIC_GRID_SIZE,
} from "../../rastering/definitions";
import {EditorMode} from "../../editor-menu/editor-mode";
import {Note} from "../../../models/note.model";
import {Node} from "../../../models/node.model";
import {UiInteractionService} from "../../../services/ui/ui.interaction.service";
import {UndoService} from "../../../services/data/undo.service";
import {CopyService} from "../../../services/data/copy.service";
import {Port} from "../../../models/port.model";
import {FilterService} from "../../../services/ui/filter.service";
import {Connection} from "../../../models/connection.model";
import {PreviewLineMode, TrainrunSectionPreviewLineView} from "./trainrunsection.previewline.view";
import {TrainrunSection} from "../../../models/trainrunsection.model";
import {Trainrun} from "../../../models/trainrun.model";
import {PositionTransformationService} from "../../../services/util/position.transformation.service";
import {AutoLayoutService} from "../../../services/util/auto-layout.service";
import {Vec2D} from "../../../utils/vec2D";
import {TrainrunSectionViewObject} from "./trainrunSectionViewObject";
import {NoteViewObject} from "./noteViewObject";
import {NodeViewObject} from "./nodeViewObject";

export class EditorKeyEvents {
  private editorMode: EditorMode;

  constructor(
    private nodeService: NodeService,
    private trainrunService: TrainrunService,
    private trainrunSectionService: TrainrunSectionService,
    private noteService: NoteService,
    private filterService: FilterService,
    private uiInteractionService: UiInteractionService,
    private undoService: UndoService,
    private copyService: CopyService,
    private svgMouseController: SVGMouseController,
    private trainrunSectionPreviewLineView: TrainrunSectionPreviewLineView,
    private positionTransformationService: PositionTransformationService,
    private autoLayoutService: AutoLayoutService,
  ) {
    this.activateMousekeyDownHandler(EditorMode.NetzgrafikEditing);
  }

  deactivateMousekeyDownHandler() {
    d3.select("body").on("keydown", () => {});
  }

  ignoreKeyEvent(event: KeyboardEvent): boolean {
    return document.body !== event.target;
  }

  activateMousekeyDownHandler(editorMode: EditorMode) {
    this.editorMode = editorMode;

    d3.select("body").on("keyup", (event: KeyboardEvent) => {
      if (this.ignoreKeyEvent(event)) {
        return;
      }
      this.forwardCtrlKeyInformation(event);
    });

    d3.select("body").on("keydown", (event: KeyboardEvent) => {
      if (this.ignoreKeyEvent(event)) {
        return;
      }

      this.forwardCtrlKeyInformation(event);

      if (this.trainrunSectionPreviewLineView.getMode() !== PreviewLineMode.NotDragging) {
        event.preventDefault();
        return;
      }

      const keycode = event.code;
      const ctrlKey = event.ctrlKey;
      const shiftKey = event.shiftKey;
      switch (keycode) {
        case "Delete":
          this.onKeyPressedDelete();
          break;
        case "Insert":
          this.onKeyPressedInsert();
          break;
        case "Escape":
          if (this.onKeyPressedEscape()) {
            event.preventDefault();
          }
          break;
        case "KeyS":
          this.onKeySPressed();
          break;
        case "KeyA":
          if (ctrlKey) {
            if (this.onSelectAll()) {
              event.preventDefault();
            }
          }
          break;
        case "KeyC":
          if (ctrlKey) {
            if (this.onCopyAllVisibleElementsToCopyCache()) {
              event.preventDefault();
            }
          }
          break;
        case "KeyV":
          if (ctrlKey) {
            if (this.onInsertAllVisibleElementsFromCopyCache()) {
              event.preventDefault();
            }
          }
          break;
        case "KeyZ":
          if (ctrlKey) {
            if (this.onRevertLastChange()) {
              event.preventDefault();
            }
          }
          break;
        case "KeyY":
          if (ctrlKey) {
            if (this.onRevertLastChange()) {
              event.preventDefault();
            }
          }
          break;
        case "KeyD":
          if (ctrlKey) {
            this.onDuplicate();
            event.preventDefault();
          }
          break;
        case "KeyL":
          this.autoLayoutService.optimizeLayout(shiftKey);
          event.preventDefault();
          break;
        case "ArrowLeft":
          this.onArrowLeft();
          event.preventDefault();
          break;
        case "ArrowUp":
          this.onArrowUp();
          event.preventDefault();
          break;
        case "ArrowRight":
          this.onArrowRight();
          event.preventDefault();
          break;
        case "ArrowDown":
          this.onArrowDown();
          event.preventDefault();
          break;

        default:
          break;
      }
    });
  }

  private onKeySPressed() {
    if (this.splitSelectedTrainrunSectionsWithNewNode()) {
      return;
    }
    this.removeNodeWithoutDeletingSections();
  }

  private removeNodeWithoutDeletingSections(): void {
    const targetNodes = this.getNodesToRemove();

    targetNodes.forEach((node) => {
      this.processNodeRemoval(node);
    });

    this.servicesUpdate();
  }

  private getNodesToRemove(): Node[] {
    let nodes = this.nodeService.getSelectedNodes();

    const isMultiNodeMode =
      this.uiInteractionService.getEditorMode() === EditorMode.MultiNodeMoving;
    if (nodes.length === 0 || !isMultiNodeMode) {
      const hoveredNodeId = this.getHoveredNodeId();
      if (hoveredNodeId !== undefined) {
        const hoveredNode = this.nodeService.getNodeFromId(hoveredNodeId);
        if (hoveredNode) {
          nodes = [hoveredNode];
        }
      }
    }
    return nodes;
  }

  private processNodeRemoval(node: Node): void {
    const nodeId = node.getId();
    const wasSelected = this.nodeService.isNodeSelected(nodeId);

    // Undock transitions but do not delete trainrun sections
    this.nodeService.deleteNodeUndockTransitions(nodeId, false, false);

    if (wasSelected) {
      this.uiInteractionService.closeNodeBaseData();
    }
  }

  private splitSelectedTrainrunSectionsWithNewNode(): boolean {
    if (!this.isMultiNodeMovingMode()) {
      return false;
    }

    const selectedSections = this.trainrunSectionService.getAllSelectedTrainrunSections();
    if (selectedSections.length === 0) {
      return false;
    }

    const groupedSections = this.groupSectionsByNodePairs(selectedSections);
    const addedNodes = this.insertStopNodes(groupedSections);

    addedNodes.forEach((n) => n.select());
    this.servicesUpdate();
    return true;
  }

  private isMultiNodeMovingMode(): boolean {
    return this.uiInteractionService.getEditorMode() === EditorMode.MultiNodeMoving;
  }

  private groupSectionsByNodePairs(sections: TrainrunSection[]): Map<string, TrainrunSection[]> {
    const map = new Map<string, TrainrunSection[]>();
    sections.forEach((ts) => {
      const srcId = ts.getSourceNode().getId();
      const trgId = ts.getTargetNode().getId();
      const [nodeA, nodeB] = srcId < trgId ? [srcId, trgId] : [trgId, srcId];
      const key = `${nodeA}-${nodeB}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(ts);
    });
    return map;
  }

  private insertStopNodes(groupedSections: Map<string, TrainrunSection[]>): Node[] {
    const addedNodes: Node[] = [];

    for (const sections of groupedSections.values()) {
      const anchor = sections[0];
      if (!anchor) continue;

      const {x, y} = this.calculateAveragePosition(sections, anchor);
      const node = this.nodeService.addNodeWithPosition(x, y);
      addedNodes.push(node);

      sections.forEach((ts) => {
        ts.setNumberOfStops(ts.getNumberOfStops() + 1);
        this.trainrunSectionService.replaceIntermediateStopWithNode(
          ts.getId(),
          Math.ceil(ts.getNumberOfStops() / 2) - 1,
          node.getId(),
        );
      });

      this.trainrunSectionService.unselectAllTrainrunSections();
    }

    return addedNodes;
  }

  private calculateAveragePosition(
    sections: TrainrunSection[],
    anchor: TrainrunSection,
  ): {x: number; y: number} {
    let x = 0,
      y = 0;
    const src = anchor.getSourceNode();
    const trg = anchor.getTargetNode();

    sections.forEach((ts) => {
      const p = ts.getPath();
      const delta = Vec2D.sub(p[3], p[0]);

      if (delta.getX() === 0) {
        x += (src.getPositionX() + trg.getPositionX()) / 2.0;
        y += this.calculateVerticalY(src, trg);
      } else if (delta.getY() === 0) {
        x += this.calculateHorizontalX(src, trg);
        y += (src.getPositionY() + trg.getPositionY()) / 2.0;
      } else {
        x += (src.getPositionX() + trg.getPositionX()) / 2.0;
        y += (src.getPositionY() + trg.getPositionY()) / 2.0;
      }
    });

    return {x: x / sections.length, y: y / sections.length};
  }

  private calculateVerticalY(src: Node, trg: Node): number {
    if (src.getPositionY() < trg.getPositionY()) {
      return (
        (src.getPositionY() +
          src.getNodeHeight() +
          trg.getPositionY() -
          NODE_MIN_HEIGHT -
          NODE_TEXT_AREA_HEIGHT) /
        2.0
      );
    }
    return (
      (src.getPositionY() +
        trg.getPositionY() +
        trg.getNodeHeight() -
        NODE_MIN_HEIGHT -
        NODE_TEXT_AREA_HEIGHT) /
      2.0
    );
  }

  private calculateHorizontalX(src: Node, trg: Node): number {
    if (src.getPositionX() < trg.getPositionX()) {
      return (src.getPositionX() + src.getNodeWidth() + trg.getPositionX() - NODE_MIN_WIDTH) / 2.0;
    }
    return (src.getPositionX() + trg.getPositionX() + trg.getNodeWidth() - NODE_MIN_WIDTH) / 2.0;
  }

  private servicesUpdate(): void {
    this.nodeService.nodesUpdated();
    this.nodeService.transitionsUpdated();
    this.trainrunService.trainrunsUpdated();
    this.trainrunSectionService.trainrunSectionsUpdated();
  }

  private forwardCtrlKeyInformation(event: KeyboardEvent) {
    const ctrlButtonState =
      event.ctrlKey && this.trainrunSectionPreviewLineView.canCombineTwoTrainruns();

    const obj1 = d3.selectAll(StaticDomTags.EDGE_LINE_PIN_DOM_REF);
    obj1.each(function () {
      d3.select(this).classed(StaticDomTags.TAG_CTRLKEY, ctrlButtonState);
    });

    const obj2 = d3.selectAll(StaticDomTags.TRANSITION_BUTTON_DOM_REF);
    obj2.each(function () {
      d3.select(this).classed(StaticDomTags.TAG_CTRLKEY, ctrlButtonState);
    });

    const obj3 = d3.selectAll(StaticDomTags.EDGE_LINE_PIN_CONNECTION_DOM_REF);
    obj3.each(function () {
      d3.select(this).classed(StaticDomTags.TAG_CTRLKEY, ctrlButtonState);
    });
  }

  private getSelectedTrainSectionId(): number {
    let selectedTrainrunSectionId: number = undefined;
    d3.select<SVGElement, TrainrunSectionViewObject>(
      StaticDomTags.EDGE_LINE_DOM_REF + "." + StaticDomTags.TAG_SELECTED,
    ).classed("KeyEventHandling", (tsvo: TrainrunSectionViewObject) => {
      if (tsvo === undefined) {
        const trainRun = this.trainrunService.getSelectedTrainrun();
        if (trainRun !== null) {
          selectedTrainrunSectionId = trainRun.getId();
        }
        return false;
      }
      if (tsvo.trainrunSection.getTrainrun().selected()) {
        selectedTrainrunSectionId = tsvo.trainrunSection.getTrainrunId();
      }
      return false;
    });
    return selectedTrainrunSectionId;
  }

  private getHoveredNoteId(): number {
    let noteHoveredId: number = undefined;
    d3.select<SVGElement, NoteViewObject>(
      StaticDomTags.NOTE_ROOT_DOM_REF + "." + StaticDomTags.TAG_HOVER,
    ).classed("KeyEventHandling", (nvo: NoteViewObject) => {
      noteHoveredId = nvo.note.getId();
      return false;
    });
    return noteHoveredId;
  }

  private getHoveredNodeId(): number {
    let hoveredNodeId: number = undefined;
    d3.select<SVGElement, NodeViewObject>(
      StaticDomTags.NODE_ROOT_DOM_REF + "." + StaticDomTags.TAG_HOVER,
    ).classed("KeyEventHandling", (nvo: NodeViewObject) => {
      hoveredNodeId = nvo.node.getId();
      return false;
    });
    return hoveredNodeId;
  }

  private onCopyAllVisibleElementsToCopyCache(): boolean {
    this.copyService.copyCurrentVisibleNetzgrafik();
    return false;
  }

  private onInsertAllVisibleElementsFromCopyCache(): boolean {
    if (!this.trainrunSectionPreviewLineView.getVariantIsWritable()) {
      return true;
    }
    this.copyService.insertCopiedNetzgrafik();
    return false;
  }

  private onArrowLeft(): boolean {
    this.positionTransformationService.alignSelectedElementsToLeftBorder();
    return true;
  }

  private onArrowUp(): boolean {
    this.positionTransformationService.alignSelectedElementsToTopBorder();
    return true;
  }

  private onArrowDown(): boolean {
    this.positionTransformationService.alignSelectedElementsToBottomBorder();
    return true;
  }

  private onArrowRight(): boolean {
    this.positionTransformationService.alignSelectedElementsToRightBorder();
    return true;
  }

  private onRevertLastChange(): boolean {
    if (!this.trainrunSectionPreviewLineView.getVariantIsWritable()) {
      return true;
    }
    this.undoService.undo();
    return true;
  }

  private onDuplicate(): boolean {
    if (!this.trainrunSectionPreviewLineView.getVariantIsWritable()) {
      return true;
    }

    if (this.doDuplicateTrainrun()) {
      return true;
    }

    if (this.doDuplicateNote()) {
      return true;
    }

    if (this.doDuplicateNode()) {
      return true;
    }

    const retNode = this.doDuplicateMultiSelectedNode();
    const retNote = this.doDuplicateMultiSelectedNote();
    return retNode || retNote;
  }

  private doDuplicateTrainrun(): boolean {
    const selectedTrainrunSectionId = this.getSelectedTrainSectionId();
    if (selectedTrainrunSectionId !== undefined) {
      this.trainrunService.duplicateTrainrunAndSections(selectedTrainrunSectionId);
      return true;
    }
    return false;
  }

  private doDuplicateNote(): boolean {
    const hoveredNoteId = this.getHoveredNoteId();
    if (hoveredNoteId !== undefined) {
      const note = this.noteService.duplicateNote(hoveredNoteId);
      this.noteService.moveNote(
        note.getId(),
        RASTERING_BASIC_GRID_SIZE,
        RASTERING_BASIC_GRID_SIZE,
        1,
        true,
      );
      this.noteService.unselectNote(note.getId());
      return true;
    }
    return false;
  }

  private doDuplicateNode(): boolean {
    if (this.isMultiNodeMovingMode()) {
      return false;
    }
    const hoveredNodeId = this.getHoveredNodeId();
    if (hoveredNodeId !== undefined) {
      const node = this.nodeService.getNodeFromId(hoveredNodeId);
      this.duplicateNode(node, node.getNodeWidth(), node.getNodeHeight());
      return true;
    }
    return false;
  }

  private doDuplicateMultiSelectedNode(): boolean {
    if (!this.isMultiNodeMovingMode()) {
      return false;
    }

    // --- Pass 1 ---- collect all selected nodes
    const collectedSelectedNodes: Node[] = [];
    this.nodeService.getNodes().forEach((n: Node) => {
      if (n.selected()) {
        collectedSelectedNodes.push(n);
      }
    });

    // --- Pass 2 ---- collect all trains that have at least one section between the two marked "nodes"
    // >>> Pass 2.1 -- filter all trainruns
    const collectedTrainrunSectionBetweenSelectedNodes = this.trainrunSectionService
      .getTrainrunSections()
      .filter(
        (ts: TrainrunSection) => ts.getSourceNode().selected() && ts.getTargetNode().selected(),
      );

    // >>> Pass 2.2 -- collect all trainruns to duplicate
    const collectedTrainruns = new Map<number, Trainrun>();
    collectedTrainrunSectionBetweenSelectedNodes.forEach((ts: TrainrunSection) => {
      if (!collectedTrainruns.has(ts.getTrainrunId())) {
        collectedTrainruns.set(ts.getTrainrunId(), ts.getTrainrun());
      }
    });

    // >>> Pass 2.3 -- remove all duplicate trainrun sections that are not encapsulated by marked nodes
    const newTrainrunSectionToModify: TrainrunSection[] = [];
    collectedTrainruns.forEach((t: Trainrun) => {
      const newTrainrun = this.trainrunService.duplicateTrainrunAndSections(t.getId(), false, "");
      const newTrainrunSections = this.trainrunSectionService.getAllTrainrunSectionsForTrainrun(
        newTrainrun.getId(),
      );
      newTrainrunSections.forEach((ts: TrainrunSection) => {
        if (ts.getSourceNode().selected() && ts.getTargetNode().selected()) {
          newTrainrunSectionToModify.push(ts);
        } else {
          this.trainrunSectionService.deleteTrainrunSection(ts.getId(), false);
        }
      });
    });

    // --- Pass 3 ----  duplicate all nodes and calculate the max notes offset (position move to)
    this.nodeService.unselectAllNodes(false);
    const newNodes = new Map<number, Node>();
    let maxW = 0;
    let maxH = 0;
    collectedSelectedNodes.forEach((n: Node) => {
      maxW = Math.max(n.getNodeWidth(), maxW);
      maxH = Math.max(n.getNodeHeight(), maxH);
    });
    collectedSelectedNodes.forEach((n: Node) => {
      const cNode = this.duplicateNode(n, maxW, maxH, false);
      newNodes.set(n.getId(), cNode);
    });

    // --- Pass 4 ---- reconnect the duplicated trainrun sections to the new created (duplicated) nodes
    // >>> Pass 4.1 -- collect non-stop information / per default stop and reconnect trainrun sections
    const srcNonStop: boolean[] = [];
    const trgNonStop: boolean[] = [];
    newTrainrunSectionToModify.forEach((ts: TrainrunSection) => {
      const newSrcNodeId = newNodes.get(ts.getSourceNode().getId())!.getId();
      const newTrgNodeId = newNodes.get(ts.getTargetNode().getId())!.getId();

      const transSrc = ts.getSourceNode().getTransition(ts.getId());
      const transNonStopSrc = transSrc !== undefined ? transSrc.getIsNonStopTransit() : false;
      const transTrg = ts.getTargetNode().getTransition(ts.getId());
      const transNonStopTrg = transTrg !== undefined ? transTrg.getIsNonStopTransit() : false;
      srcNonStop.push(transNonStopSrc);
      trgNonStop.push(transNonStopTrg);

      this.trainrunSectionService.reconnectTrainrunSection(
        ts.getSourceNodeId(),
        newTrgNodeId,
        ts.getId(),
        ts.getTargetNodeId(),
        ts.getSourceNodeId(),
        false,
      );
      this.trainrunSectionService.reconnectTrainrunSection(
        ts.getTargetNodeId(),
        newSrcNodeId,
        ts.getId(),
        ts.getTargetNodeId(),
        ts.getSourceNodeId(),
        false,
      );
    });

    // >>> Pass 4.2 -- correct the non-stop transitions
    newTrainrunSectionToModify.forEach((ts: TrainrunSection, index: number) => {
      const tSrc = ts.getSourceNode().getTransition(ts.getId());
      if (tSrc !== undefined && srcNonStop[index]) {
        tSrc.setIsNonStopTransit(srcNonStop[index]);
      }
      const tTrg = ts.getTargetNode().getTransition(ts.getId());
      if (tTrg !== undefined && trgNonStop[index]) {
        tTrg.setIsNonStopTransit(trgNonStop[index]);
      }
    });

    // --- Pass 5 ---- unselect all trainruns and select all new nodes
    this.trainrunService.unselectAllTrainruns(false);
    newNodes.forEach((n: Node) => n.select());

    // Publish the batched changes once after the duplicate is complete.
    this.nodeService.transitionsUpdated();
    this.nodeService.connectionsUpdated();
    this.trainrunSectionService.trainrunSectionsUpdated();
    this.trainrunService.trainrunsUpdated();
    return true;
  }

  private doDuplicateMultiSelectedNote(): boolean {
    if (this.uiInteractionService.getEditorMode() !== EditorMode.MultiNodeMoving) {
      return false;
    }

    // --- Pass 1 ---- collect all selected nodes
    const collectedSelectedNotes: Note[] = [];
    this.noteService.getNotes().forEach((n: Note) => {
      if (n.selected()) {
        collectedSelectedNotes.push(n);
      }
    });

    // --- Pass 2 ----  duplicate all notes and calculate the max notes offset (position move to)
    this.noteService.unselectAllNotes(false);
    const newNotes: Note[] = [];
    let maxW = 0;
    let maxH = 0;
    collectedSelectedNotes.forEach((n: Note) => {
      maxW = Math.max(n.getWidth(), maxW);
      maxH = Math.max(n.getHeight(), maxH);
    });
    collectedSelectedNotes.forEach((n: Note) => {
      const cNode = this.noteService.duplicateNote(n.getId());
      this.noteService.moveNote(
        cNode.getId(),
        maxW + RASTERING_BASIC_GRID_SIZE,
        maxH + RASTERING_BASIC_GRID_SIZE,
        1,
        true,
        false,
      );
      newNotes.push(cNode);
    });

    // --- Pass 3 ---- unselect all trainruns and select all new nodes
    newNotes.forEach((n: Note) => {
      n.select();
    });

    // call update - due of disabled update (signal) in the passes 1-5
    this.noteService.notesUpdated();
    return true;
  }

  private duplicateNode(
    node: Node,
    nodeWidth: number,
    nodeHeight: number,
    enforceUpdate = true,
  ): Node {
    const newNode = this.nodeService.duplicateNode(node.getId(), enforceUpdate);
    this.nodeService.changeNodePosition(
      newNode.getId(),
      newNode.getPositionX() + nodeWidth + RASTERING_BASIC_GRID_SIZE,
      newNode.getPositionY() + nodeHeight + RASTERING_BASIC_GRID_SIZE,
      true,
      enforceUpdate,
    );
    this.nodeService.unselectNode(newNode.getId(), enforceUpdate);
    return newNode;
  }

  private onKeyPressedEscape(): boolean {
    this.trainrunService.unselectAllTrainruns();
    if (this.editorMode === EditorMode.MultiNodeMoving) {
      this.nodeService.unselectAllNodes();
      this.noteService.unselectAllNotes();
      this.trainrunSectionService.unselectAllTrainrunSections();
    }
    return true;
  }

  private onSelectAll(): boolean {
    if (
      this.uiInteractionService.getEditorMode() === EditorMode.MultiNodeMoving ||
      this.uiInteractionService.getEditorMode() === EditorMode.NetzgrafikEditing
    ) {
      this.uiInteractionService.setEditorMode(EditorMode.MultiNodeMoving);
      this.noteService.getNotes().forEach((n: Note) => {
        if (this.filterService.filterNote(n)) {
          n.select();
        }
      });
      this.nodeService.getNodes().forEach((n: Node) => {
        if (this.filterService.filterNode(n)) {
          n.select();
        }
      });
      this.noteService.notesUpdated();
      this.nodeService.nodesUpdated();
    }
    return true;
  }

  private onKeyPressedInsert(): boolean {
    if (!this.trainrunSectionPreviewLineView.getVariantIsWritable()) {
      return true;
    }

    if (this.onDuplicate()) {
      return true;
    }

    const mousePosition = this.svgMouseController.getLastMousePosition();
    const hoveredNodeId = this.getHoveredNodeId();
    if (hoveredNodeId !== undefined) {
      this.nodeService.addNodeWithPosition(mousePosition.getX(), mousePosition.getY());
      return true;
    }

    this.nodeService.addNodeWithPosition(mousePosition.getX(), mousePosition.getY());
    return true;
  }

  private deleteTrainrunsAndEmptyNodesIfFilteringActive() {
    let visibleTrainrunSections: number[] = [];
    this.nodeService.getNodes().forEach((n: Node) => {
      if (n.selected()) {
        n.getPorts().forEach((p: Port) => {
          const trainrunSection = p.getTrainrunSection();
          if (this.filterService.filterTrainrunsection(trainrunSection)) {
            visibleTrainrunSections.push(trainrunSection.getId());
          }
        });
      }
    });
    visibleTrainrunSections = visibleTrainrunSections.filter((v, i, a) => a.indexOf(v) === i);
    visibleTrainrunSections.forEach((trainrunSectionId: number) => {
      this.trainrunSectionService.deleteTrainrunSection(trainrunSectionId, false);
    });

    let selectedNodeDeleted = false;
    this.nodeService.getNodes().forEach((n: Node) => {
      if (n.selected()) {
        if (n.getPorts().length === 0) {
          if (n.selected()) {
            selectedNodeDeleted = true;
          }
          this.nodeService.deleteNode(n.getId(), false);
        }
      }
    });

    this.netzgrafikElementsUpdated();

    if (selectedNodeDeleted) {
      this.uiInteractionService.closeNodeBaseData();
    }
  }

  private removeAllSelectedNodes() {
    let selectedNodeDeleted = false;
    this.nodeService.getNodes().forEach((n: Node) => {
      if (n.selected()) {
        selectedNodeDeleted = true;
        this.nodeService.deleteNode(n.getId(), false);
      }
    });

    if (selectedNodeDeleted) {
      this.uiInteractionService.closeNodeBaseData();
    }
  }

  private removeAllSelectedNotes() {
    this.noteService.getNotes().forEach((n: Note) => {
      if (n.selected()) {
        this.noteService.deleteNote(n.getId(), false);
      }
    });
  }

  private onKeyPressedDelete(): boolean {
    if (!this.trainrunSectionPreviewLineView.getVariantIsWritable()) {
      return true;
    }
    const selectedTrainrunSectionId = this.getSelectedTrainSectionId();

    const connections = this.nodeService.getAllSelectedConnections();
    if (connections.length > 0) {
      connections.forEach((connection: Connection) => {
        const node = this.nodeService
          .getNodes()
          .find(
            (n: Node) =>
              n.getConnections().find((c) => connection.getId() === c.getId()) !== undefined,
          );
        if (node !== undefined) {
          this.nodeService.removeConnectionFromNode(node.getId(), connection.getId());
        }
      });
      return true;
    }

    if (this.editorMode === EditorMode.MultiNodeMoving) {
      /* handle Nodes and Trainruns/TrainrunSections */
      if (this.filterService.isAnyFilterActive()) {
        this.deleteTrainrunsAndEmptyNodesIfFilteringActive();
      } else {
        this.removeAllSelectedNodes();
      }
      this.netzgrafikElementsUpdated();

      /* handle Notes */
      this.removeAllSelectedNotes();
      this.noteService.notesUpdated();

      this.uiInteractionService.setEditorMode(EditorMode.NetzgrafikEditing);
      this.nodeService.unselectAllNodes();
      this.noteService.unselectAllNotes();
      return true;
    }

    if (selectedTrainrunSectionId !== undefined) {
      this.trainrunSectionService.deleteAllTrainrunSectionsOfTrainrun(selectedTrainrunSectionId);
      return true;
    }

    const hoveredNodeId = this.getHoveredNodeId();
    if (hoveredNodeId !== undefined) {
      const selNodeDelete = this.nodeService.isNodeSelected(hoveredNodeId);
      this.nodeService.deleteNode(hoveredNodeId);
      if (selNodeDelete) {
        this.uiInteractionService.closeNodeBaseData();
      }
      return true;
    }

    const hoveredNoteId = this.getHoveredNoteId();
    if (hoveredNoteId !== undefined) {
      this.noteService.deleteNote(hoveredNoteId);
      return true;
    }

    return false;
  }

  private netzgrafikElementsUpdated() {
    this.trainrunSectionService.trainrunSectionsUpdated();
    this.trainrunService.trainrunsUpdated();
    this.nodeService.transitionsUpdated();
    this.nodeService.connectionsUpdated();
    this.nodeService.nodesUpdated();
  }
}
